import {
  faArrowDownToLine,
  faArrowLeft,
  faArrowRight,
  faArrowUpRight,
  faCircleCheck,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/cloudflare";
import { motion } from "framer-motion";

import { Quote } from "~/components/blog/quote";
import { EcosystemLogo } from "~/components/logos/ecosystem";
import { CallToAction } from "~/components/site/call-to-action";
import { ExternalLink } from "~/components/site/external-link";
import { item, Layout } from "~/components/site/layout";
import { ROUTE_DATA } from "~/lib/constants";
import { getMeta } from "~/lib/get-meta";
import { getUrls } from "~/lib/get-urls";
import type { Step } from "~/routes/case-studies";

const { desc, path, title, ogImage } = ROUTE_DATA.etherfuse;

const quote: Quote = {
  quote:
    "The MXNe stablecoin, made possible through our partnership with Brale, has the potential to revolutionize the way we transact and capture value. This is a significant step forward, benefiting the communities who create, develop, promote, and use it. Brale is a genuinely legendary team, and we look forward to  creating a platform that allows anyone, anywhere in the world, to develop a Stablecoin in any currency.",
  author: "David Taylor",
  photo: "/assets/photos/david-taylor.png",
  title: "Founder & CEO",
  company: "Etherfuse",
};

const steps: Step[] = [
  {
    image: "/assets/case-studies/etherfuse/etherfuse-step-1.svg",
    text: "U.S. senders convert USD to MXNe",
  },
  {
    image: "/assets/case-studies/etherfuse/etherfuse-step-2.svg",
    text: "Sender processes payouts to Mexico in MXNe",
  },
  {
    image: "/assets/case-studies/etherfuse/etherfuse-step-3.svg",
    text: "Mexican business receives MXNe and converts to local pesos",
  },
];

const features: string[] = [
  "Stablecoin issuance",
  "Exchange listing",
  "Partner onboarding",
  "Onramp / offramp",
  "Token management",
  "Reserve management",
  "Program pages",
  "Multi-chain gateway",
  "Teams",
  "MPC signing",
  "Program monitor",
];

export function loader({ request }: LoaderFunctionArgs) {
  return {
    urls: getUrls({ path, request }),
  };
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return data
    ? [
        ...getMeta({ desc, title, urls: data.urls }),
        { property: "og:image", content: `${data.urls.base}${ogImage}` },
        { name: "twitter:image", content: `${data.urls.base}${ogImage}` },
      ]
    : [];
};

export default function Etherfuse(): JSX.Element {
  return (
    <Layout>
      <div className="section-first blog-post print:py-6">
        <div className="container">
          <motion.div variants={item} className="pb-12 print:pb-6">
            <a
              href="/case-studies"
              className="inline-flex items-center gap-2 mb-6 no-underline"
            >
              <FontAwesomeIcon
                className="h-4 w-4 text-secondary print:hidden"
                icon={faArrowLeft}
              />
              <div className="eyebrow !m-0 text-secondary">
                <span className="hidden print:inline">Brale - </span>
                <span>Case studies</span>
              </div>
            </a>
            <h1 className="h2 mt-0 mb-6 print:!text-2xl print:mb-0">
              How Brale helped Etherfuse launch MXNe, the first Mexican Peso
              stablecoin on Solana and Stellar
            </h1>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-3 items-start lg:gap-16">
            <div className="lg:sticky top-8 mb-16 flex flex-col gap-4 print:mb-6">
              <motion.div
                variants={item}
                className="bg-secondary round p-4 lg:p-6 border border-primary"
              >
                <div className="flex flex-col items-start gap-y-6">
                  <div className="flex flex-col items-start gap-y-6 print:flex-row-reverse print:gap-4 print:items-center">
                    <span className="rounded-full border border-primary px-3 py-1 text-sm font-medium text-black dark:text-white">
                      DeFi Protocol
                    </span>
                    <EcosystemLogo ecosystemId={"etherfuse"} />
                  </div>
                  <div className="w-full flex flex-col sm:flex-row sm:justify-between lg:flex-col xl:flex-row gap-4 print:flex-row print:justify-between">
                    <div className="flex-grow print:flex print:items-end print:gap-2">
                      <div className="text-2xl font-semibold text-primary">
                        21 days
                      </div>
                      <div className="!mb-0">Time to launch</div>
                    </div>
                    <div className="flex-grow print:flex print:items-end print:gap-2">
                      <div className="text-2xl font-semibold text-primary">
                        3
                      </div>
                      <div className="!mb-0">Chains</div>
                    </div>
                    <div className="flex-grow print:flex print:items-end print:gap-2">
                      <div className="text-2xl font-semibold text-primary">
                        3
                      </div>
                      <div className="!mb-0">Markets</div>
                    </div>
                  </div>
                </div>
              </motion.div>
              <motion.div
                variants={item}
                className="flex flex-col md:flex-row lg:flex-col gap-y-6 items-start md:items-center md:justify-between lg:items-start bg-secondary round p-4 lg:p-6 border border-primary print:flex-row print:justify-between print:items-center"
              >
                <div className="flex items-center gap-3">
                  <img
                    src="/assets/stablecoins/mxne-logo.png"
                    className="h-8 w-8 rounded-full border border-primary"
                    alt="MXNe logo"
                  />
                  <span className="text-2xl font-semibold text-primary">
                    MXNe
                  </span>
                  <span className="text-2xl text-secondary font-normal whitespace-nowrap md:hidden xl:inline">
                    Real MXN
                  </span>
                </div>
                <div className="flex flex-wrap gap-3 print:hidden">
                  <a
                    className="btn secondary text-sm no-underline global-hover-transition !bg-white dark:!bg-gray-900 flex items-center gap-x-2"
                    title="Learn more about MXNe"
                    href="/stablecoins/MXNe"
                  >
                    <span>Learn more</span>
                    <FontAwesomeIcon
                      className="h-4 w-4 m-[1px]"
                      icon={faArrowRight}
                    />
                  </a>
                  <ExternalLink
                    className="btn secondary text-sm no-underline global-hover-transition !bg-white dark:!bg-gray-900 flex items-center gap-x-2"
                    title="Learn more about USDLGO"
                    href="https://app.brale.xyz/buy/signup/MXNe?ref=case-study"
                  >
                    <span>Apply to buy</span>
                    <FontAwesomeIcon
                      className="h-4 w-4 m-[1px]"
                      icon={faArrowUpRight}
                    />
                  </ExternalLink>
                  <ExternalLink
                    className="btn secondary text-sm no-underline global-hover-transition !bg-white dark:!bg-gray-900 flex items-center gap-x-2"
                    title="Download Case Study PDF"
                    href="/assets/case-studies/etherfuse/Etherfuse-Brale-Case-Study.pdf"
                  >
                    <span>Download PDF</span>
                    <FontAwesomeIcon
                      className="h-4 w-4 m-[1px]"
                      icon={faArrowDownToLine}
                    />
                  </ExternalLink>
                </div>
              </motion.div>
            </div>
            <motion.div
              variants={item}
              className="col-span-2 flex flex-col gap-y-6"
            >
              <div>
                <div className="round border border-primary not-prose mb-10 bg-black overflow-clip print:hidden">
                  <img
                    alt="Etherfuse | Brale Case Study"
                    src="/assets/case-studies/etherfuse/cover.svg"
                  />
                </div>
                <h2 className="mt-0 print:!text-xl">Summary</h2>
                <p className="subheading mt-2 mb-12 print:!text-base print:mb-8">
                  Etherfuse partnered with Brale to launch MXNe, the first
                  Mexican Peso stablecoin on Solana, Stellar, and Base. MXNe is
                  designed to foster local economic growth, reduce transaction
                  costs, and ensure transparent, secure value capture within the
                  Mexican community. MXNe was brought to market in less than a
                  month, enabling Mexican businesses and individuals to benefit
                  from a stable, community-driven currency.
                </p>
                <h2 className="mt-0 print:!text-xl">Problem</h2>
                <p className="subheading mt-2 mb-12 lg:mb-0 print:!text-base print:mb-0">
                  Etherfuse needed a regulated partner to issue a stablecoin
                  backed by the Mexican Peso to support local economic growth in
                  Mexico.
                </p>
                <h3 className="h4 print:!text-lg">The situation</h3>
                <p className="print:!text-sm">
                  Etherfuse identified an opportunity for Mexican businesses,
                  individuals, and policymakers to benefit from a local
                  stablecoin would retain value within the community and reduce
                  transaction costs.
                </p>
                <h3 className="h4 print:!text-lg">The challenges</h3>
                <p className="print:!text-sm">
                  Etherfuse needed an issuance partner to support a Mexican Peso
                  stablecoin with integrated U.S. dollar on and offramps,
                  reserves transparency, and reporting.
                </p>
                <p className="print:!text-sm">
                  Additionally, they needed a partner who could manage reserves
                  in CETES, the on-chain Mexcian treasury bonds issued by
                  Etherfuse.
                </p>
                <div>
                  <Quote quote={quote} />
                </div>
                <h2 className="print:!text-xl">How Brale helped</h2>
                <div>
                  <h3 className="h4 print:!text-lg">
                    Regulated issuance stack
                  </h3>
                  <p className="print:!text-sm">
                    To deploy a regulated stablecoin, Etherfuse initially
                    explored various options but found that developing a new
                    entity to manage the reserves, issuance, and reporting was
                    both time-consuming and cost-prohibitive.
                  </p>
                  <div className="bg-secondary round p-6 lg:p-8 border border-primary flex flex-col sm:grid sm:grid-cols-2 gap-x-12 gap-y-6 mt-8 mb-12 print:p-4 print:my-6">
                    <div>
                      <h4 className="h5 mt-0 print:!text-base">Solution</h4>
                      <p className="m-0 print:!text-sm">
                        Brale&apos;s regulated issuance stack enabled Etherfuse
                        to issue MXNe with integrated on and offramps, reserves
                        management, and reporting.
                      </p>
                    </div>
                    <div>
                      <h4 className="h5 mt-0 print:!text-base">Outcome</h4>
                      <p className="m-0 print:!text-sm">
                        By partnering with Brale, Etherfuse was able to swiftly
                        bring MXNe to market, securely backed by CETES tokens,
                        and fully compliant with regulatory requirements.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="print:break-after-page">
                  <h3 className="h4 print:!text-lg">Currency conversion</h3>
                  <p className="print:!text-sm">
                    Etherfuse needed a partner who could provide cross-currency
                    swaps between USD stablecoins and MXNe to support
                    cross-border payments.
                  </p>
                  <div className="bg-secondary round p-6 lg:p-8 border border-primary flex flex-col sm:grid sm:grid-cols-2 gap-x-12 gap-y-6 mt-8 mb-12 print:p-4 print:my-6">
                    <div>
                      <h4 className="h5 mt-0 print:!text-base">Solution</h4>
                      <p className="m-0 print:!text-sm">
                        Brale provides low-fee stablecoin swaps between
                        Brale-issued stablecoins, USDC, and PYUSD with MXNe.
                      </p>
                    </div>
                    <div>
                      <h4 className="h5 mt-0 print:!text-base">Outcome</h4>
                      <p className="m-0 print:!text-sm">
                        Etherfuse was able to expand access to MXNe to U.S.
                        based businesses to support cross-border payments and
                        lower transactions fees for receivers in Mexico.
                      </p>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="h4 print:!text-lg">
                    Multi-chain implementation
                  </h3>
                  <p className="print:!text-sm">
                    Etherfuse aspired to deploy MXNe on multiple blockchains
                    from day one to make available to partners in different
                    ecosystems.
                  </p>
                  <div className="bg-secondary round p-6 lg:p-8 border border-primary flex flex-col sm:grid sm:grid-cols-2 gap-x-12 gap-y-6 mt-8 mb-12 print:p-4 print:my-6">
                    <div>
                      <h4 className="h5 mt-0 print:!text-base">Solution</h4>
                      <p className="m-0 print:!text-sm">
                        Brale supports the issuance of regulated stablecoins on
                        10+ blockchains leveraging audited token standards on
                        each chain, and retains the ability for Etherfuse to
                        expand to new chains quickly.
                      </p>
                    </div>
                    <div>
                      <h4 className="h5 mt-0 print:!text-base">Outcome</h4>
                      <p className="m-0 print:!text-sm">
                        By working with Brale, Etherfuse was able to issue MXNe
                        on Solana, Stellar, and Base immediately, engaging
                        multiple communities.
                      </p>
                    </div>
                  </div>
                </div>
                <h2 className="print:!text-xl">
                  MXNe, the first Mexican Peso stablecoin on Solana and Stellar
                </h2>
                <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-x-6 xl:gap-x-8 gap-y-8 border border-primary round p-6 xl:p-10 bg-secondary print:grid-cols-3 print:p-4 print:mt-6">
                  {steps.map((s, i) => (
                    <div key={i} className="flex flex-col">
                      <div className="flex-shrink-0 flex items-center justify-center">
                        <img
                          alt={`Graphic showing ${s.text}`}
                          src={s.image}
                          className="w-full not-prose"
                        />
                      </div>
                      <div className="mt-8 flex-grow border-t-2 border-primary print:mt-0">
                        <div className="mt-4 print:!text-sm">{s.text}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <h2 className="print:!text-xl">Features used</h2>
                <div className="bg-secondary round p-6 lg:p-8 border border-primary">
                  <ul className="gap-y-3 leading-6 grid sm:grid-cols-2 xl:grid-cols-3 m-0 p-0 print:grid-cols-3">
                    {features.map((feature, i) => (
                      <li
                        key={i}
                        className="flex gap-x-3 m-0 p-0 text-base print:!text-sm print:gap-x-2 print:items-center"
                      >
                        <FontAwesomeIcon
                          className="h-6 w-6 text-green-500 print:h-4 print:w-4"
                          icon={faCircleCheck}
                        />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                <CallToAction
                  seoRef="customer-story"
                  heading="Empower your community through a local currency stablecoin"
                  subheading="Collaborate with our team to support payments in your community"
                  button="Schedule meeting"
                  ctaUrl="/contact"
                  className="print:hidden"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
